import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { List } from 'immutable';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { Table } from 'common/components';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IPastDue, IPastDueImt } from 'common/components/PersonProfile/interfaces';
import { CustomTheme } from 'common/ui/interfaces';
import { FilterTypes, IFilterSettings } from 'common/interfaces/filter';
import { PastDueActions, PastDueResolutions } from 'common/components/PersonProfile/constants';
import { StepContext } from 'common/createContext/stepContext';
import { useAppDispatch } from 'store/hooks';
import { usePersonSelectorTemplate } from 'common/components/PersonProfile/hooks/usePersonSelector';
import { getBillingItemDescription, getCurrentPageList } from '../utils';
import { makeTableParams } from 'common/utils/http';
import * as actions from 'common/components/PersonProfile/state/actions';
import * as selectors from 'common/components/PersonProfile/state/selectors';
import { PeakModules } from 'common/constants/peakModules';
import messages from 'common/components/PersonProfile/messages';
import tableHeaders from 'common/messages/tableHeaders';
import tableFilters from 'common/messages/tableFilters';
import { DEFAULT_DATE_FORMAT } from 'common/constants/dateFormats';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import useMultipleSelectTableControl, {
  IMultipleSelectTableControlProps,
} from 'common/hooks/useMultipleSelectTableControl';
import { formatNumberToPrice, formatPrice } from 'common/utils';
import { TableOrderByParams } from 'common/constants';
import { removeTaxAmount } from 'modules/services/utils/billingUtils';
import useInitial from 'common/hooks/useInitial';
import usePermissionContext from 'common/hooks/context/usePermissionContext';

const useStyles = makeStyles((theme: CustomTheme) => ({
  tableWrapper: {
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.borderColor?.main}`,
  },
  totalLabel: {
    fontSize: '1.25rem',
    textAlign: 'right',
    marginTop: theme.spacing(1.5),
  },
  noPastDuesBox: {
    height: '200px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const tableFilterSettings: IFilterSettings[] = [
  {
    name: 'range',
    title: <FormattedMessage {...tableFilters.period} />,
    type: FilterTypes.DATE_RANGE,
    options: {
      startDate: null,
      endDate: null,
    },
  },
];

const headerOptions: IHeadCell[] = [
  { id: 'status', label: <FormattedMessage {...tableHeaders.status} />, sort: false },
  { id: TableOrderByParams.DATE, label: <FormattedMessage {...tableHeaders.date} />, sort: true },
  { id: 'price', label: <FormattedMessage {...tableHeaders.price} />, align: 'right', sort: false },
  { id: 'packageBillingInfo', label: <FormattedMessage {...tableHeaders.name} />, sort: false },
];

interface IPastDuesStep {
  personId: number;
  membershipId?: string;
  module: PeakModules;
  isMembershipCard?: boolean;
}

const PastDuesStep: FC<IPastDuesStep> = ({
  personId,
  membershipId,
  module,
  isMembershipCard = false,
}) => {
  const dispatch = useAppDispatch();
  const usePersonSelector = usePersonSelectorTemplate(personId);
  const { MEMBER_WAIVE_FEE } = usePermissionContext();

  const pastDueBillings: List<IPastDueImt> = usePersonSelector(selectors.selectPastDueBillings);
  const isPastDueBillingsLoading: boolean = usePersonSelector(
    selectors.selectPastDueBillingsLoading,
  );

  const initialTableParams = useInitial(() => {
    return makeTableParams([], null, { orderBy: TableOrderByParams.DATE, order: 'desc' });
  });

  const [tableParams, setTableParams] = useState(initialTableParams);
  const [pastDuesData, setPastDuesData] = useState<Partial<IMultipleSelectTableControlProps>>({
    isAllSelected: false,
    selected: [],
  });

  const isInitialPastDuesExist = useRef(false);
  const prevTableParams = useRef(tableParams);

  const onChangeTableParamsSubscriber = (rowsData: IMultipleSelectTableControlProps) => {
    setPastDuesData(rowsData);
  };

  const [timezoneMoment] = useTimezoneMoment();
  const { onBack, renderFooter, onNext } = useContext(StepContext);
  const convertedPastDues = useMemo(() => pastDueBillings?.toJS() || [], [pastDueBillings]);
  const classes = useStyles();

  const fetchPastDues = useCallback(
    (params: ITableParams) => {
      if (membershipId) {
        dispatch(
          actions.fetchPastDuesForCancel(personId, membershipId, params, module, isMembershipCard),
        );
      } else {
        dispatch(actions.fetchPastDuesForReactivate(personId, params, module, isMembershipCard));
      }
    },
    [dispatch, membershipId, module, personId, isMembershipCard],
  );

  useEffect(() => {
    fetchPastDues(initialTableParams);
  }, [fetchPastDues, initialTableParams]);

  useEffect(() => {
    if (!isInitialPastDuesExist.current && pastDueBillings?.size) {
      isInitialPastDuesExist.current = true;
    }
  }, [pastDueBillings]);

  const { ref } = useMultipleSelectTableControl({
    onChangeTableParamsSubscriber,
  });

  const handleChangeTableParams = useCallback(
    (tableProps: ITableParams): void => {
      const { page: prevPage, perPage: prevPerPage } = prevTableParams.current;
      const { page, perPage } = tableProps;

      setTableParams(tableProps);

      prevTableParams.current = tableProps;

      if (prevPage !== page || prevPerPage !== perPage) {
        return;
      }

      fetchPastDues(tableProps);
    },
    [fetchPastDues],
  );

  const handleResolveStep = (): void => {
    let pastDueIds;

    if (pastDuesData.isAllSelected && !pastDuesData.excluded?.length) {
      pastDueIds = convertedPastDues.map(pastDueItem => pastDueItem.id);
    } else if (pastDuesData.excluded?.length) {
      pastDueIds = convertedPastDues.reduce(
        (acc, pastDueItem) =>
          pastDuesData.excluded?.includes(pastDueItem.id) ? acc : [...acc, pastDueItem],
        [],
      );
    } else {
      pastDueIds = pastDuesData.selected;
    }

    if (onNext) {
      onNext({ pastDues: pastDueIds });
    }
  };

  const currentPagePastDues = useMemo(
    () => getCurrentPageList(pastDueBillings?.toJS() || [], tableParams.page, tableParams.perPage),
    [pastDueBillings, tableParams.page, tableParams.perPage],
  );

  const checkIsPastDueItemAdded = useCallback(
    (pastDueId: string): boolean =>
      Boolean(
        pastDuesData.selected?.includes(pastDueId) ||
          (pastDuesData.isAllSelected && !pastDuesData.excluded?.includes(pastDueId)),
      ),
    [pastDuesData],
  );

  const pastDuesTotal = useMemo(() => {
    if (pastDuesData.isAllSelected && !pastDuesData.excluded?.length) {
      return convertedPastDues.reduce((totalAmount, pastDueItem) => {
        const { id, amount, revenueCode } = pastDueItem;

        const pastDueAmount = revenueCode?.totalTax
          ? removeTaxAmount(amount, revenueCode.totalTax)
          : amount;

        return totalAmount + (pastDuesData.excluded?.includes(id) ? 0 : pastDueAmount);
      }, 0);
    }

    if (pastDuesData.excluded?.length) {
      return convertedPastDues.reduce((totalAmount, pastDueItem) => {
        const { id, amount, revenueCode } = pastDueItem;

        const pastDueAmount = revenueCode?.totalTax
          ? removeTaxAmount(amount, revenueCode.totalTax)
          : amount;

        return totalAmount + (pastDuesData.excluded?.includes(id) ? 0 : pastDueAmount);
      }, 0);
    }

    return pastDuesData.selected?.reduce((totalAmount, pastDueItemId) => {
      const { amount, revenueCode } = convertedPastDues.find(
        pastDueItem => pastDueItem.id === pastDueItemId,
      );

      const pastDueAmount = revenueCode?.totalTax
        ? removeTaxAmount(amount, revenueCode.totalTax)
        : amount;

      return totalAmount + pastDueAmount;
    }, 0);
  }, [convertedPastDues, pastDuesData]);

  const tableRows: ITableRow[] = useMemo(
    () =>
      currentPagePastDues.map((pastDue: IPastDue) => ({
        id: pastDue.id || '',
        cells: [
          {
            label: '',
            cellComponent: (
              <Typography
                variant="button"
                color={checkIsPastDueItemAdded(pastDue.id || '') ? 'primary' : 'error'}
              >
                {PastDueResolutions.translate(
                  checkIsPastDueItemAdded(pastDue.id || '')
                    ? PastDueActions.Keep
                    : PastDueActions.Waive,
                )}
              </Typography>
            ),
          },
          {
            align: 'left',
            variant: 'h5',
            label: pastDue.paymentDate
              ? timezoneMoment(pastDue.paymentDate).format(DEFAULT_DATE_FORMAT)
              : '',
          },
          {
            align: 'right',
            variant: 'h5',
            label: `$${formatNumberToPrice(
              pastDue.revenueCode?.totalTax
                ? removeTaxAmount(pastDue.amount, pastDue.revenueCode.totalTax)
                : pastDue.amount,
            )}`,
          },
          {
            align: 'left',
            label: getBillingItemDescription(pastDue.package),
          },
        ],
      })),
    [checkIsPastDueItemAdded, currentPagePastDues, timezoneMoment],
  );

  return (
    <>
      {pastDueBillings?.size || isInitialPastDuesExist.current ? (
        <>
          <Box className={classes.tableWrapper}>
            <Table
              activeSelect={MEMBER_WAIVE_FEE}
              showPerPageSelect
              initialSelected={convertedPastDues.map(item => item.id)}
              initialTotalRows={convertedPastDues.length}
              rows={tableRows}
              isLoading={isPastDueBillingsLoading}
              onChangeParams={handleChangeTableParams}
              headerOptions={headerOptions}
              totalRows={pastDueBillings?.size}
              filters={tableFilterSettings}
              refMultipleSelectParams={ref}
              tableParams={tableParams}
            />
          </Box>

          <Typography component="p" className={classes.totalLabel}>
            <FormattedMessage
              {...messages.pastDuesModalTotalLabel}
              values={{
                price: (
                  <Typography component="span" variant="h3">
                    {formatPrice(pastDuesTotal)}
                  </Typography>
                ),
              }}
            />
          </Typography>
        </>
      ) : (
        <Box className={classes.noPastDuesBox}>
          <Typography variant="h2" color="textSecondary">
            <FormattedMessage {...messages.pastDuesModalNoPastDues} />
          </Typography>
        </Box>
      )}

      {renderFooter ? renderFooter(onBack, handleResolveStep) : null}
    </>
  );
};

export default React.memo(PastDuesStep);
